import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat, fn, hash } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import Form from "discourse/components/form";
import GroupSelector from "discourse/components/group-selector";
import { ajax } from "discourse/lib/ajax";
import { extractError } from "discourse/lib/ajax-error";
import Group from "discourse/models/group";
import ComboBox from "discourse/select-kit/components/combo-box";
import EmailGroupUserChooser from "discourse/select-kit/components/email-group-user-chooser";
import DModal from "discourse/ui-kit/d-modal";
import { i18n } from "discourse-i18n";
import BulkInviteSampleCsvFile from "../bulk-invite-sample-csv-file.gjs";
import CsvUploader from "../csv-uploader.gjs";

const DEFAULT_ATTENDANCE = "going";

export default class PostEventBulkInvite extends Component {
  @service toasts;

  @tracked flash = null;

  formApi;

  data = {
    invitees: [{ identifier: null, attendance: DEFAULT_ATTENDANCE }],
    recurring: false,
  };

  get bulkInviteStatuses() {
    return [
      {
        label: i18n("discourse_post_event.models.invitee.status.going"),
        name: "going",
      },
      {
        label: i18n("discourse_post_event.models.invitee.status.not_going"),
        name: "not_going",
      },
      {
        label: i18n("discourse_post_event.models.invitee.status.interested"),
        name: "interested",
      },
    ];
  }

  @action
  groupFinder(term) {
    return Group.findAll({ term, ignore_automatic: true });
  }

  @action
  registerApi(api) {
    this.formApi = api;
  }

  @action
  csvAdditionalParams() {
    return { recurring: this.formApi?.get("recurring") ?? false };
  }

  @action
  addInvite(addItemToCollection) {
    const invitees = this.formApi?.get("invitees") ?? [];
    const attendance = invitees.at(-1)?.attendance || DEFAULT_ATTENDANCE;
    addItemToCollection("invitees", { identifier: null, attendance });
  }

  @action
  async removeInvite(remove, index, addItemToCollection) {
    await remove(index);

    // always keep at least one row so the collection never collapses
    if ((this.formApi?.get("invitees") ?? []).length === 0) {
      await addItemToCollection("invitees", {
        identifier: null,
        attendance: DEFAULT_ATTENDANCE,
      });
    }
  }

  @action
  setUserIdentifier(field, selected) {
    field.set(selected[0]);
  }

  @action
  setGroupIdentifier(field, _, groupNames) {
    field.set(groupNames[0]);
  }

  @action
  async sendBulkInvites(data) {
    try {
      const response = await ajax(
        `/discourse-post-event/events/${this.args.model.event.id}/bulk-invite.json`,
        {
          type: "POST",
          dataType: "json",
          contentType: "application/json",
          data: JSON.stringify({
            invitees: data.invitees,
            recurring: data.recurring,
          }),
        }
      );

      if (response.success) {
        this.args.closeModal();
      }
    } catch (e) {
      this.flash = extractError(e);
    }
  }

  @action
  uploadDone() {
    this.args.closeModal();
    this.toasts.success({
      duration: "short",
      data: { message: i18n("discourse_post_event.bulk_invite_modal.success") },
    });
  }

  <template>
    <DModal
      class="post-event-bulk-invite"
      @closeModal={{@closeModal}}
      @flash={{this.flash}}
      @title={{i18n "discourse_post_event.bulk_invite_modal.title"}}
    >
      <:body>
        <Form
          class="bulk-invites-form"
          @data={{this.data}}
          @onRegisterApi={{this.registerApi}}
          @onSubmit={{this.sendBulkInvites}}
          as |form|
        >
          <p class="bulk-event-help">
            {{i18n
              (concat
                "discourse_post_event.bulk_invite_modal.description_"
                @model.event.status
              )
            }}
          </p>

          <div class="bulk-invites">
            <h3>{{i18n
                "discourse_post_event.bulk_invite_modal.inline_title"
              }}</h3>

            <form.Collection @name="invitees" as |collection index|>
              <div class="bulk-invite-row">
                <collection.Field
                  class="bulk-invite-identifier-field"
                  @name="identifier"
                  @showTitle={{false}}
                  @title={{i18n
                    (if
                      @model.event.isPrivate
                      "discourse_post_event.bulk_invite_modal.group_selector_placeholder"
                      "discourse_post_event.bulk_invite_modal.user_selector_placeholder"
                    )
                  }}
                  @type="custom"
                  @validation="required"
                  as |field|
                >
                  <field.Control>
                    {{#if @model.event.isPrivate}}
                      <GroupSelector
                        class="bulk-invite-identifier"
                        @groupFinder={{this.groupFinder}}
                        @groupNames={{field.value}}
                        @onChangeCallback={{fn this.setGroupIdentifier field}}
                        @placeholderKey="discourse_post_event.bulk_invite_modal.group_selector_placeholder"
                        @single={{true}}
                      />
                    {{/if}}
                    {{#if @model.event.isPublic}}
                      <EmailGroupUserChooser
                        class="bulk-invite-identifier"
                        @onChange={{fn this.setUserIdentifier field}}
                        @options={{hash
                          maximum=1
                          filterPlaceholder="discourse_post_event.bulk_invite_modal.user_selector_placeholder"
                        }}
                        @value={{field.value}}
                      />
                    {{/if}}
                  </field.Control>
                </collection.Field>

                <collection.Field
                  @name="attendance"
                  @showTitle={{false}}
                  @title={{i18n
                    "discourse_post_event.bulk_invite_modal.attendance_label"
                  }}
                  @type="custom"
                  as |field|
                >
                  <field.Control>
                    <ComboBox
                      class="bulk-invite-attendance"
                      @content={{this.bulkInviteStatuses}}
                      @nameProperty="name"
                      @onChange={{field.set}}
                      @value={{field.value}}
                      @valueProperty="name"
                    />
                  </field.Control>
                </collection.Field>

                <form.Button
                  class="remove-bulk-invite"
                  @action={{fn
                    this.removeInvite
                    collection.remove
                    index
                    form.addItemToCollection
                  }}
                  @icon="trash-can"
                />
              </div>
            </form.Collection>

            <form.Actions class="bulk-invite-actions">
              <form.Button
                class="add-bulk-invite"
                @action={{fn this.addInvite form.addItemToCollection}}
                @icon="plus"
                @label="discourse_post_event.bulk_invite_modal.add_attendee"
              />
              {{#if @model.isRecurring}}
                <form.Field
                  class="bulk-invite-recurrence"
                  @format="full"
                  @name="recurring"
                  @title={{i18n
                    "discourse_post_event.bulk_invite_modal.recurring"
                  }}
                  @type="checkbox"
                  as |field|
                >
                  <field.Control />
                </form.Field>
              {{/if}}
              <form.Submit
                class="send-bulk-invites"
                @label="discourse_post_event.bulk_invite_modal.send_bulk_invites"
              />
            </form.Actions>
          </div>

          <div class="csv-bulk-invites">
            <h3>{{i18n "discourse_post_event.bulk_invite_modal.csv_title"}}</h3>

            <div class="bulk-invite-actions">
              <BulkInviteSampleCsvFile />

              <CsvUploader
                @additionalParams={{this.csvAdditionalParams}}
                @i18nPrefix="discourse_post_event.bulk_invite_modal"
                @uploadDone={{this.uploadDone}}
                @uploadUrl={{concat
                  "/discourse-post-event/events/"
                  @model.event.id
                  "/csv-bulk-invite"
                }}
              />
            </div>
          </div>
        </Form>
      </:body>
    </DModal>
  </template>
}
